import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { formatDistanceToNowStrict } from "date-fns";
import { ChevronDown, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TextOverflow } from "~/components/ui/text-overflow";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useFetcher } from "~/hooks/useFetcher";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";
import { cn } from "~/lib/utils";
import {
  AccessRequestStatus,
  CohortAccessRequest,
  PaginatedAccessRequests,
} from "~/pages/manifold-nextgen/components/cohorts/interfaces/access_requests";
import { type StreamEvent } from "~/pages/manifold-nextgen/components/cohorts/interfaces/export_stream";
import {
  PanelPosition,
  useResizablePanelWidth,
} from "~/pages/manifold-nextgen/hooks/useResizablePanelWidth";
import { apiMutate, apiStreamClient, getApiErrorMessage, isApiError } from "~/utils/apiClient";
import { formatDateTime } from "~/utils/date";
import { readStreamEvents } from "~/utils/streamReader";
import { ChildDatasetAccessRequestDetailsPanel } from "./ChildDatasetAccessRequestDetailsPanel";
import { ChildDatasetAccessRequestStatusBadge } from "./ChildDatasetAccessRequestStatusBadge";
import { type ChildDatasetExportPanelState } from "./ChildDatasetExportPanel";

const PAGE_SIZE = 25;
const PANEL_MIN_WIDTH_PX = 480;
const PANEL_MAX_WIDTH_PX = 960;
const PANEL_DEFAULT_WIDTH_PX = 480;

const STATUS_FILTER_OPTIONS: (AccessRequestStatus | null)[] = [
  null,
  AccessRequestStatus.PENDING,
  AccessRequestStatus.IN_REVIEW,
  AccessRequestStatus.EXPORT_PROCESSING,
  AccessRequestStatus.EXPORT_SUCCESSFUL,
  AccessRequestStatus.EXPORT_FAILED,
  AccessRequestStatus.APPROVED,
  AccessRequestStatus.DENIED,
  AccessRequestStatus.CANCELLED,
];

// Preview users see these export statuses masked to IN_REVIEW, so filtering by them is meaningless.
const PREVIEW_HIDDEN_STATUS_FILTERS = new Set<AccessRequestStatus | null>([
  AccessRequestStatus.EXPORT_PROCESSING,
  AccessRequestStatus.EXPORT_SUCCESSFUL,
  AccessRequestStatus.EXPORT_FAILED,
]);

const formatStatusFilterLabel = (filter: AccessRequestStatus | null) =>
  filter
    ? filter
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    : "All";

// ── Main component ────────────────────────────────────────────────────────────

export const ChildDatasetAccessRequestsList = () => {
  const { datasetId } = useParams();
  const { data: dataset } = useDataset();
  const isPreview = dataset?.access_level === DatasetAccessLevel.PREVIEW;
  const { showError, showSuccess } = useToast();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<AccessRequestStatus | null>(null);

  const statusFilterOptions = isPreview
    ? STATUS_FILTER_OPTIONS.filter((filter) => !PREVIEW_HIDDEN_STATUS_FILTERS.has(filter))
    : STATUS_FILTER_OPTIONS;

  const params = new URLSearchParams();
  if (datasetId) params.set("parent_dataset_id", datasetId);
  if (statusFilter) params.set("status_filter", statusFilter);
  params.set("limit", String(PAGE_SIZE));
  params.set("offset", String((page - 1) * PAGE_SIZE));

  function handleStatusFilterChange(filter: AccessRequestStatus | null) {
    setStatusFilter(filter);
    setPage(1);
  }

  const { data, isLoading, mutate } = useFetcher<PaginatedAccessRequests>(
    datasetId ? `/api/cohort-explorer/v1/access-requests?${params.toString()}` : null
  );

  const requests = data?.items ?? [];
  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  // AbortController and submission guard must survive re-renders without triggering them — legitimate ref use.
  const abortRef = useRef<AbortController | null>(null);
  const isSubmittingRef = useRef(false);

  const [exportPanel, setExportPanel] = useState<ChildDatasetExportPanelState | null>(null);
  const [confirmDeny, setConfirmDeny] = useState<CohortAccessRequest | null>(null);
  const [denyReason, setDenyReason] = useState("");
  const [selectedRequest, setSelectedRequest] = useState<CohortAccessRequest | null>(null);
  const [isDenying, setIsDenying] = useState(false);
  const [isReviewing, setIsReviewing] = useState<string | null>(null);
  const [isApproving, setIsApproving] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  async function handleReview(request: CohortAccessRequest) {
    setIsReviewing(request.id);
    try {
      await apiMutate(`/api/cohort-explorer/v1/access-requests/${request.id}/review`, "POST");
      await mutate();
      setSelectedRequest((prev) =>
        prev?.id === request.id ? { ...prev, status: AccessRequestStatus.IN_REVIEW } : prev
      );
    } catch (err) {
      showError(isApiError(err) ? getApiErrorMessage(err) : "Failed to move request to review");
    } finally {
      setIsReviewing(null);
    }
  }

  async function handleCancel(request: CohortAccessRequest) {
    setIsCancelling(request.id);
    try {
      await apiMutate(
        `/api/cohort-explorer/v1/access-requests/${request.id}/cancel`,
        "POST",
        undefined,
        {
          expects204: true,
        }
      );
      await mutate();
      setSelectedRequest((prev) =>
        prev?.id === request.id ? { ...prev, status: AccessRequestStatus.CANCELLED } : prev
      );
      showSuccess("Access request cancelled");
    } catch (err) {
      showError(isApiError(err) ? getApiErrorMessage(err) : "Failed to cancel request");
    } finally {
      setIsCancelling(null);
    }
  }

  function defaultExportPanel(request: CohortAccessRequest): ChildDatasetExportPanelState {
    if (request.child_dataset_id) {
      return {
        requestId: request.id,
        datasetName: request.cohort_name,
        streamStatus: "done",
        progressItems: [],
        resultDatasetId: request.child_dataset_id,
        resultWarnings: [],
        streamError: null,
      };
    }
    return {
      requestId: request.id,
      datasetName: request.cohort_name,
      streamStatus: "idle",
      progressItems: [],
      resultDatasetId: null,
      resultWarnings: [],
      streamError: null,
    };
  }

  function handleExportClick(request: CohortAccessRequest) {
    setExportPanel(defaultExportPanel(request));
  }

  function handleCancelExport() {
    abortRef.current?.abort();
    setExportPanel(null);
  }

  async function handleExportSubmitForPanel(panelOverride: ChildDatasetExportPanelState) {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setExportPanel({
      ...panelOverride,
      streamStatus: "streaming",
      progressItems: [],
      streamError: null,
    });

    try {
      const response = await apiStreamClient(
        `/api/cohort-explorer/v1/access-requests/${panelOverride.requestId}/export`,
        {
          method: "POST",
          body: JSON.stringify({
            name: panelOverride.datasetName.trim(),
            add_user_to_child_dataset: false,
          }),
          signal: controller.signal,
        }
      );

      let succeeded = false;
      let progressReceived = false;

      for await (const event of readStreamEvents(response, controller.signal)) {
        const data = event as StreamEvent;
        if (data.kind === "progress") {
          progressReceived = true;
          setExportPanel((prev) => {
            if (!prev) return null;
            const updated = prev.progressItems.map((item, i) =>
              i === prev.progressItems.length - 1 ? { ...item, status: "done" as const } : item
            );
            return {
              ...prev,
              progressItems: [...updated, { message: data.message, status: "loading" }],
            };
          });
        } else if (data.kind === "result") {
          setExportPanel((prev) => {
            if (!prev) return null;
            return {
              ...prev,
              progressItems: prev.progressItems.map((item) => ({
                ...item,
                status: "done" as const,
              })),
              resultDatasetId: data.id,
              resultWarnings: data.warnings ?? [],
            };
          });
          succeeded = true;
        } else if (data.kind === "error") {
          throw new Error(data.message);
        }
      }

      if (succeeded) {
        setExportPanel((prev) => (prev ? { ...prev, streamStatus: "done" } : null));
        await mutate();
      } else {
        throw new Error(
          progressReceived
            ? "The export did not complete. The server may have encountered an issue — please try again."
            : "The server did not respond. Please try again."
        );
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      let streamError: string;
      if (err instanceof TypeError) {
        streamError =
          "The export was interrupted by a network error. Check your connection and try again.";
      } else if (isApiError(err)) {
        streamError = getApiErrorMessage(err);
      } else {
        streamError = err instanceof Error ? err.message : "An unexpected error occurred.";
      }
      setExportPanel((prev) => (prev ? { ...prev, streamStatus: "error", streamError } : null));
    } finally {
      isSubmittingRef.current = false;
    }
  }

  async function handleExportSubmit() {
    if (!exportPanel || isSubmittingRef.current) return;
    await handleExportSubmitForPanel(exportPanel);
  }

  async function handleApprove(request: CohortAccessRequest) {
    setIsApproving(request.id);
    try {
      await apiMutate(`/api/cohort-explorer/v1/access-requests/${request.id}/approve`, "POST");
      await mutate();
      setSelectedRequest((prev) =>
        prev?.id === request.id ? { ...prev, status: AccessRequestStatus.APPROVED } : prev
      );
      setExportPanel(null);
      showSuccess("Request approved — user added to dataset");
    } catch (err) {
      showError(isApiError(err) ? getApiErrorMessage(err) : "Failed to approve request");
    } finally {
      setIsApproving(null);
    }
  }

  const {
    width: panelWidth,
    handleDragStart,
    contentRef,
  } = useResizablePanelWidth({
    minWidth: PANEL_MIN_WIDTH_PX,
    maxWidth: PANEL_MAX_WIDTH_PX,
    initialWidth: PANEL_DEFAULT_WIDTH_PX,
    position: PanelPosition.RIGHT,
  });

  async function handleDeny() {
    if (!confirmDeny) return;
    setIsDenying(true);
    try {
      await apiMutate(
        `/api/cohort-explorer/v1/access-requests/${confirmDeny.id}/deny`,
        "POST",
        { status_reason: denyReason.trim() },
        {
          expects204: true,
        }
      );
      await mutate();
      setSelectedRequest((prev) =>
        prev?.id === confirmDeny.id
          ? { ...prev, status: AccessRequestStatus.DENIED, status_reason: denyReason.trim() }
          : prev
      );
      showSuccess("Request denied successfully");
    } catch {
      showError("Failed to deny request");
    } finally {
      setIsDenying(false);
      setConfirmDeny(null);
      setDenyReason("");
    }
  }

  const panelContent = selectedRequest && (
    <ChildDatasetAccessRequestDetailsPanel
      request={selectedRequest}
      isPreview={isPreview}
      onClose={() => setSelectedRequest(null)}
      confirmDeny={confirmDeny}
      isDenying={isDenying}
      denyReason={denyReason}
      onDenyReasonChange={setDenyReason}
      onDenyClick={setConfirmDeny}
      onDenyConfirm={() => void handleDeny()}
      onDenyCancel={() => {
        setConfirmDeny(null);
        setDenyReason("");
      }}
      onApproveClick={handleExportClick}
      exportPanel={exportPanel?.requestId === selectedRequest.id ? exportPanel : null}
      onExportPanelDatasetNameChange={(name) =>
        setExportPanel((prev) =>
          prev?.requestId === selectedRequest.id
            ? { ...prev, datasetName: name }
            : { ...defaultExportPanel(selectedRequest), datasetName: name }
        )
      }
      onExportPanelExport={() => {
        if (!exportPanel || exportPanel.requestId !== selectedRequest.id) {
          const panel = defaultExportPanel(selectedRequest);
          setExportPanel(panel);
          void handleExportSubmitForPanel(panel);
        } else {
          void handleExportSubmit();
        }
      }}
      onExportPanelApprove={() => void handleApprove(selectedRequest)}
      onExportPanelCancel={handleCancelExport}
      isApproving={isApproving === selectedRequest.id}
      onCancelClick={() => void handleCancel(selectedRequest)}
      isCancelling={isCancelling === selectedRequest.id}
    />
  );
  const isPanelOpen = panelContent != null;

  return (
    <div className="-m-6 flex h-[calc(100%+3rem)] gap-6">
      <div
        className={cn(
          "relative min-h-0 min-w-0 flex-1 overflow-y-auto pt-6 pl-6",
          !isPanelOpen && "flex justify-center"
        )}
      >
        <div className="flex w-full flex-col gap-4" style={{ maxWidth: NEXTGEN_MAX_CONTENT_WIDTH }}>
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-semibold">Access Requests</h2>
          </div>

          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs">
                  <Filter className="size-4" aria-hidden="true" />
                  {formatStatusFilterLabel(statusFilter)}
                  <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuRadioGroup
                  value={statusFilter ?? "all"}
                  onValueChange={(value) =>
                    handleStatusFilterChange(
                      value === "all" ? null : (value as AccessRequestStatus)
                    )
                  }
                >
                  {statusFilterOptions.map((filter) => (
                    <DropdownMenuRadioItem key={filter ?? "all"} value={filter ?? "all"}>
                      {formatStatusFilterLabel(filter)}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {isLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : requests && requests.length > 0 ? (
            <div className="rounded-lg border border-border">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[20ch]">Requested By</TableHead>
                    <TableHead className="w-36">Created</TableHead>
                    <TableHead className="w-[18ch]">Cohort</TableHead>
                    <TableHead className="w-24">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow
                      key={request.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => {
                        if (isReviewing === request.id) return;
                        setSelectedRequest(request);
                        if (!isPreview && request.status === AccessRequestStatus.PENDING) {
                          void handleReview(request);
                        }
                      }}
                    >
                      <TableCell className="whitespace-normal">
                        <TextOverflow autoTooltip lines={2} className="font-medium">
                          {request.created_by}
                        </TextOverflow>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger asChild>
                            <span>
                              {formatDistanceToNowStrict(new Date(request.created_at), {
                                addSuffix: true,
                              })}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {formatDateTime(request.created_at, { hour: "numeric" })}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="whitespace-normal">
                        <TextOverflow autoTooltip lines={2}>
                          {request.cohort_name}
                        </TextOverflow>
                      </TableCell>
                      <TableCell>
                        <ChildDatasetAccessRequestStatusBadge status={request.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
              <p className="text-sm text-muted-foreground">
                {statusFilter
                  ? `No access requests for this dataset with status: ${formatStatusFilterLabel(statusFilter)}.`
                  : "No cohort access requests for this dataset."}
              </p>
            </div>
          )}

          {data && data.total > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.total)} of{" "}
                {data.total}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="size-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages}
                  >
                    Next
                    <ChevronRight className="size-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div
        aria-hidden={!isPanelOpen}
        inert={!isPanelOpen}
        className="relative flex shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out"
        style={{ width: isPanelOpen ? panelWidth : 0 }}
      >
        <div
          onMouseDown={handleDragStart}
          className="group absolute inset-y-0 left-0 z-20 w-1.5 cursor-col-resize"
        >
          <div className="pointer-events-none absolute inset-y-0 left-0 w-px bg-border transition-all group-hover:w-0.5 group-hover:bg-primary group-active:w-0.5 group-active:bg-primary" />
        </div>
        <div
          ref={contentRef}
          className="min-w-0 shrink-0 overflow-hidden"
          style={{ width: panelWidth }}
        >
          {panelContent}
        </div>
      </div>
    </div>
  );
};
